import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AtSign } from 'lucide-react';
import { useRememberedState, useRememberedScroll } from '../hooks/useRemembered';
import { UserProfile } from '../types';
import { useIsMobile } from '../hooks/useIsMobile';
import { useProjectLocks } from '../hooks/useProjectLocks';
import { useNotifications } from '../hooks/useNotifications';
import { useUnbookedSentReports } from '../hooks/useUnbookedSentReports';
import { useDashboardLayout } from '../hooks/useDashboardLayout';
import { useGlassDesign } from '../design/glassMode';
import { istUebungsprojekt } from '../services/uebung/uebungService';
import { parseDateLocal } from '../services/dateService';
import { buildTaskGroups, countTasks, flattenTasks, selectTaskProjects } from '../services/taskList';
import { sortUnbookedReports } from '../services/unbookedReportsLogic';
import type { AppNotification } from '../services/notificationStore';
import type { ListProject } from '../services/listProject';
import { NotificationList } from './notifications/NotificationList';
import { DashboardHeader } from './dashboard/DashboardHeader';
import { KpiKey, KpiStrip } from './dashboard/KpiStrip';
import { AgendaPanel } from './dashboard/AgendaPanel';
import { TaskPanel } from './dashboard/TaskPanel';
import { UnbookedPanel } from './dashboard/UnbookedPanel';
import { ProjectsPanel, ProjectsView } from './dashboard/ProjectsPanel';
import { SubstitutionPanel } from './dashboard/SubstitutionPanel';
import { SendCenterPanel } from './dashboard/SendCenterPanel';
import { MobileDashboard } from './dashboard/MobileDashboard';
import { DashboardGrid } from './dashboard/DashboardGrid';
import { DashboardCustomizer } from './dashboard/DashboardCustomizer';
import { MobileCustomizeSheet } from './dashboard/MobileCustomizeSheet';
import {
    DEFAULT_PRESET, KpiId, PresetId, WidgetId, Zone, applyPreset, hideWidget, isVisible, nudgeWidget, showWidget, toggleKpi,
} from './dashboard/dashboardLayout';
import { dropWidget, mobileNudge } from './dashboard/dashboardEditing';
import {
    DashboardProject, Kpis, NearbyHit, buildAgenda, buildProgressRows, calendarWeekOf, countStatus, dayKey,
    greetingFor, nearbyProjects, selectRelevantProjects,
} from './dashboard/dashboardModel';
import { LinkButton, Panel, PanelHeader } from './dashboard/dashboardUi';
import { scrollToPanel } from './dashboard/dashboardTones';
import { usePlanungAmpeln } from './dashboard/usePlanungAmpeln';
import { ampelEingangFuer, baueHandlungsbedarf, waehlePlanungProjekte } from './dashboard/planungHandlungsbedarf';
import type { PlanungHandlungsbedarfStand } from './dashboard/PlanungAmpelPanel';
import { ergaenzePlanungAufgaben } from './planungsphase/planungAufgaben';

/**
 * `projects` ist die Projektliste der App — Listenauszuege, kein volles
 * Projekt (listProject.ts). Gespeichert wird von hier aus nichts: Verschieben
 * und Ueberspringen laufen ueber die App-Handler, die das Projekt per Kennung
 * neu laden.
 */
interface Props {
    projects: ReadonlyArray<ListProject>;
    userProfile: UserProfile | null;
    onOpenProject: (project: ListProject) => void;
    onRefresh: () => void;
    onPostpone: (project: ListProject) => void;
    onSkip: (project: ListProject) => void;
    onNavigate?: (view: string) => void;
}

/**
 * Das Mitarbeiter-Cockpit — Startseite jeder Nicht-Freelancer-Rolle.
 *
 * Diese Datei haelt nur noch Zustand und Datenfluss; gerechnet wird in
 * `dashboard/dashboardModel.ts` und `services/taskList.ts` (beide ohne React,
 * mit Tests), gezeichnet in `dashboard/*`. Mitteilungen kommen aus dem
 * gemeinsamen Speicher (`useNotifications`), sodass Glocke, Spalte und
 * Handy-Navigation immer denselben Stand zeigen.
 *
 * Welcher Baustein wo steht, entscheidet das persoenliche Layout
 * (`useDashboardLayout`, auf dem Server gespeichert): `renderWidget`
 * zeichnet einen Baustein je Kennung, `DashboardGrid` verteilt sie auf die
 * Zonen, der Anpassen-Modus (`editing`) legt Rahmen und Ablagen darueber.
 */
export const DashboardView: React.FC<Props> = ({ projects: alleProjekte, userProfile, onOpenProject, onPostpone, onSkip, onNavigate }) => {
    const isMobile = useIsMobile();
    // Optik „Glas" (nur wenn gewaehlt, sonst unveraendert).
    const glass = useGlassDesign();
    const userId = userProfile?.id;
    const myEmail = (userProfile?.email || '').trim().toLowerCase();

    // ── Uhr: Minutentakt fuer Gruss und Zeit; der Tag (todayKey) ist der
    // einzige Zeitwert, an dem die Memos haengen — sie rechnen also nur um
    // Mitternacht neu, nicht bei jedem Tick.
    const [clock, setClock] = useState(() => new Date());
    useEffect(() => { const t = setInterval(() => setClock(new Date()), 60000); return () => clearInterval(t); }, []);
    const todayKey = dayKey(clock);
    const todayStart = useMemo(() => { const d = parseDateLocal(todayKey) || new Date(); d.setHours(0, 0, 0, 0); return d; }, [todayKey]);
    const nowMs = clock.getTime();

    // ── Gemerkte Bedienzustaende (Schluessel wie vor dem Umbau, damit die
    // Nutzer ihre Einstellungen behalten).
    const [showSende, setShowSende] = useRememberedState('dashboard.showSende', false);
    const [emailSearch, setEmailSearch] = useRememberedState('dashboard.emailSearch', '');
    const [midView, setMidView] = useRememberedState<ProjectsView>('dashboard.midView', 'gantt');
    const [subCollapsed, setSubCollapsed] = useRememberedState('dashboard.subCollapsed', true);
    const [mobileSearch, setMobileSearch] = useRememberedState('dashboard.mobileSearch', '');
    const [mobileRestOpen, setMobileRestOpen] = useRememberedState('dashboard.mobile.restOpen', false);
    const mobileScrollRef = useRememberedScroll('dashboard.mobile.scroll');
    const desktopScrollRef = useRememberedScroll('dashboard.scroll');
    // Vollbild des Zeitstrahls ist fluechtig — nach einem Neuladen bewusst zu.
    const [projectsExpanded, setProjectsExpanded] = useState(false);

    const [ganttServiceOverride, setGanttServiceOverride] = useState<Record<string, string>>({});
    const [nearby, setNearby] = useState<NearbyHit[]>([]);
    const [highlightKey, setHighlightKey] = useState<string | null>(null);

    // ── Persoenliches Layout (Server) und Anpassen-Modus (fluechtig)
    const { layout, setLayout, saveState, retrySave } = useDashboardLayout(userId, userProfile?.settings);
    const [editing, setEditing] = useState(false);
    const [draggingId, setDragging] = useState<WidgetId | null>(null);
    // Ausserhalb des Modus haengt nie etwas am Zeiger — abgeleitet, kein Effekt.
    const dragging = editing ? draggingId : null;

    const moveTo = useCallback((id: WidgetId, zone: Zone, index: number) => setLayout(l => dropWidget(l, id, zone, index)), [setLayout]);
    const nudge = useCallback((id: WidgetId, delta: -1 | 1) => setLayout(l => nudgeWidget(l, id, delta)), [setLayout]);
    const nudgeOnPhone = useCallback((id: WidgetId, delta: -1 | 1) => setLayout(l => mobileNudge(l, id, delta)), [setLayout]);
    const hide = useCallback((id: WidgetId) => setLayout(l => hideWidget(l, id)), [setLayout]);
    const show = useCallback((id: WidgetId) => setLayout(l => showWidget(l, id)), [setLayout]);
    const toggleWidget = useCallback((id: WidgetId, visible: boolean) => setLayout(l => (visible ? showWidget(l, id) : hideWidget(l, id))), [setLayout]);
    const toggleTile = useCallback((id: KpiId) => setLayout(l => toggleKpi(l, id)), [setLayout]);
    const choosePreset = useCallback((id: PresetId) => setLayout(applyPreset(id)), [setLayout]);
    const resetLayout = useCallback(() => setLayout(applyPreset(DEFAULT_PRESET)), [setLayout]);
    const endDrag = useCallback(() => setDragging(null), []);
    const toggleEditing = useCallback(() => {
        // Ein offenes Vollbild laege sonst unbedienbar unter den inerten Rahmen.
        setProjectsExpanded(false);
        setEditing(v => !v);
    }, []);

    // Esc verlaesst den Anpassen-Modus am PC; das Handy-Blatt hat Esc selbst.
    // Ein Menue im Rahmen faengt sein Esc vorher ab (defaultPrevented).
    useEffect(() => {
        if (!editing || isMobile) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) setEditing(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [editing, isMobile]);

    // ── Projekte
    // ÜBUNGSPROJEKTE FALLEN IN KEINE AUSWERTUNG; `visible` speist Naehe und
    // Mitteilungs-Links (alle Projekte, die der Nutzer sieht), `all` das
    // Cockpit selbst (Verantwortung + Vertretung).
    const visible = useMemo<DashboardProject[]>(() => alleProjekte.filter(p => !istUebungsprojekt(p)), [alleProjekte]);
    const { all, subs, inspections } = useMemo(() => selectRelevantProjects(visible, { id: userId, email: myEmail }), [visible, userId, myEmail]);
    const projectsById = useMemo(() => new Map(visible.map(p => [p.id, p] as const)), [visible]);
    const stats = useMemo(() => countStatus(all, subs), [all, subs]);

    // Sperren aller hier erreichbaren Projekte in EINEM Abruf.
    const { locks, nowMs: lockNowMs } = useProjectLocks(all.map(p => p.id));

    // ── Ableitungen
    // Agenda nur aus Projekten mit Begehungs-Zustaendigkeit: wer nur
    // Verantwortliche Planungsphase ist, bekommt deren Termine nicht als faellig.
    const agenda = useMemo(() => buildAgenda(inspections, userId, todayKey), [inspections, userId, todayKey]);

    // ── Planungs-Ampeln (Stufe 1.5): EINE Sammelabfrage fuer die Projekte, deren
    // Planungsphase mich angeht (Verantwortliche Planungsphase, sonst Besitzer/
    // Hauptverantwortlicher). Speist die Liste „Planungsphase — Handlungsbedarf"
    // im Aufgaben-Baustein und schaerft die Planungsaufgaben der Aufgabenliste.
    const planungProjekte = useMemo(() => waehlePlanungProjekte(visible, { id: userId, email: myEmail }), [visible, userId, myEmail]);
    const planungEingaenge = useMemo(() => planungProjekte.map(ampelEingangFuer), [planungProjekte]);
    const planungAmpeln = usePlanungAmpeln(planungEingaenge, todayKey);
    const planungZeilen = useMemo(
        () => baueHandlungsbedarf(planungProjekte, planungAmpeln.ampeln, planungAmpeln.weiter),
        [planungProjekte, planungAmpeln.ampeln, planungAmpeln.weiter],
    );
    const planung = useMemo<PlanungHandlungsbedarfStand>(
        () => ({ zeilen: planungZeilen, laden: planungAmpeln.laden, fehler: planungAmpeln.fehler }),
        [planungZeilen, planungAmpeln.laden, planungAmpeln.fehler],
    );
    // Sprung ins Modul: erst das Projekt oeffnen (laedt den Bericht, zeigt den
    // Hub), dann dem Hub den Modulwechsel melden — derselbe Weg, den
    // App.executeModuleNavigation fuer den Sendeverlauf nimmt (postMessage
    // NAVIGATE). Der kurze Aufschub laesst den Hub erst seinen Empfaenger
    // anmelden. Scheitert das Oeffnen (gesperrtes Projekt), verhallt die
    // Nachricht ohne Empfaenger.
    // Der Parameter steht im Typ des App-Handlers; die Planungszeilen reichen
    // dieselben Listeneintraege herein (dashboardModel fuehrt sie als
    // DashboardProject).
    const openPlanung = useCallback((p: ListProject) => {
        Promise.resolve(onOpenProject(p))
            .then(() => { window.setTimeout(() => window.postMessage({ type: 'NAVIGATE', module: 'planung' }, '*'), 100); })
            .catch((e: unknown) => { console.warn('Projekt konnte nicht geoeffnet werden.', e); });
    }, [onOpenProject]);

    const taskGroups = useMemo(
        () => ergaenzePlanungAufgaben(buildTaskGroups(selectTaskProjects(alleProjekte, userProfile), todayStart, userProfile), planungAmpeln.ampeln, planungAmpeln.weiter),
        [alleProjekte, userProfile, todayStart, planungAmpeln.ampeln, planungAmpeln.weiter],
    );
    const totals = useMemo(() => countTasks(taskGroups), [taskGroups]);
    const flatTasks = useMemo(() => flattenTasks(taskGroups), [taskGroups]);
    const progressRows = useMemo(() => buildProgressRows(all, ganttServiceOverride), [all, ganttServiceOverride]);

    // ── Mitteilungen (gemeinsamer Speicher) und versendete Berichte ohne Buchung
    const notifications = useNotifications(userId);
    const unbooked = useUnbookedSentReports(!!userId);
    const unbookedRows = useMemo(() => {
        const ids = new Set(all.map(p => p.id));
        return sortUnbookedReports(unbooked.rows.filter(r => ids.has(r.project_id)));
    }, [unbooked.rows, all]);

    const kpis = useMemo<Kpis>(() => ({
        overdue: agenda.overdue,
        today: agenda.today,
        week: agenda.week,
        weekEnd: agenda.weekEnd,
        tasksOpen: totals.total,
        tasksUrgent: totals.urgent,
        unread: notifications.unreadCount,
        unbooked: unbooked.isLoading && unbooked.rows.length === 0 ? null : unbookedRows.length,
    }), [agenda, totals, notifications.unreadCount, unbooked.isLoading, unbooked.rows.length, unbookedRows.length]);

    // ── Standort (nur Handy): alle sichtbaren Projekte im Umkreis von 2 km.
    useEffect(() => {
        if (!isMobile || !('geolocation' in navigator)) return;
        navigator.geolocation.getCurrentPosition(
            pos => setNearby(nearbyProjects(visible, pos.coords.latitude, pos.coords.longitude)),
            () => { /* Standort verweigert/kein Fix — Abschnitt bleibt leer */ },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
        );
    }, [isMobile, visible]);

    // ── Mitteilungen quittieren (nur der Status, kein Projekt wird geschrieben)
    const markRead = useCallback((n: AppNotification) => notifications.markRead(n), [notifications]);
    const markAllRead = useCallback(() => notifications.markAllRead(), [notifications]);

    // Robust navigation: onNavigate zuerst, sonst den Seitenleisten-Knopf suchen.
    const navigateTo = useCallback((view: string) => {
        if (onNavigate) { onNavigate(view); return; }
        const labels: Record<string, string[]> = { tasks: ['Aufgaben', 'Tasks'] };
        const targets = labels[view] || [view];
        const buttons = document.querySelectorAll('button, a, [role="tab"], [data-view]');
        for (const btn of buttons) {
            const text = (btn.textContent || '').trim();
            if (btn.getAttribute('data-view') === view || targets.some(t => text.toLowerCase().includes(t.toLowerCase()))) {
                (btn as HTMLElement).click(); return;
            }
        }
        console.warn(`Navigation to "${view}" failed — no matching button found in DOM`);
    }, [onNavigate]);

    const greeting = greetingFor(clock.getHours());
    const dateStr = clock.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
    const timeStr = clock.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    const kw = calendarWeekOf(todayKey);

    // ── Desktop-Ziele fuer Kacheln und Glocke: je Baustein seine Huelle
    // (DashboardGrid), je Plan-Gruppe ihr Element (AgendaPanel).
    const widgetEls = useRef(new Map<WidgetId, HTMLElement>());
    const widgetRef = useCallback((id: WidgetId, el: HTMLElement | null) => {
        if (el) widgetEls.current.set(id, el); else widgetEls.current.delete(id);
    }, []);
    const groupEls = useRef(new Map<string, HTMLElement>());
    const highlightTimer = useRef<number | null>(null);
    const highlight = (key: string) => {
        setHighlightKey(key);
        if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
        highlightTimer.current = window.setTimeout(() => setHighlightKey(null), 1600);
    };
    useEffect(() => () => { if (highlightTimer.current) window.clearTimeout(highlightTimer.current); }, []);

    // Zum Baustein rollen — ist er ausgeblendet, in die passende Liste
    // wechseln statt eines toten Klicks.
    const jumpTo = (id: WidgetId, fallbackView: string) => {
        const el = widgetEls.current.get(id);
        if (el && isVisible(layout, id)) scrollToPanel(el); else navigateTo(fallbackView);
    };

    const onKpi = (key: KpiKey) => {
        if (key === 'tasks') { jumpTo('tasks', 'tasks'); return; }
        if (key === 'unread') { jumpTo('notifications', 'tasks'); return; }
        if (key === 'unbooked') { jumpTo('unbooked', 'projects'); return; }
        // Ueberfaellig/Heute: zur Gruppe, sonst zum Plan als Ganzem.
        const groupKey = key === 'overdue' ? 'overdue' : key === 'today' ? 'today' : null;
        const target = groupKey ? groupEls.current.get(groupKey) : null;
        if (target && isVisible(layout, 'agenda')) { scrollToPanel(target); highlight(groupKey!); return; }
        jumpTo('agenda', 'projects');
    };

    const substitutionPanel = subs.length > 0 ? (
        <SubstitutionPanel
            subs={subs}
            myEmail={myEmail}
            userId={userId}
            now={clock}
            locks={locks}
            lockNowMs={lockNowMs}
            collapsed={subCollapsed}
            onToggle={() => setSubCollapsed(v => !v)}
            onOpen={onOpenProject}
            projectsById={projectsById}
        />
    ) : null;

    if (isMobile) {
        return (
            <MobileDashboard
                glass={glass}
                userProfile={userProfile ? { id: userProfile.id, first_name: userProfile.first_name, email: userProfile.email } : null}
                greeting={greeting}
                dateStr={dateStr}
                kw={kw}
                timeStr={timeStr}
                stats={stats}
                all={all}
                visible={visible}
                agenda={agenda}
                kpis={kpis}
                tasks={flatTasks}
                totals={totals}
                planung={planung}
                onOpenPlanung={openPlanung}
                notifications={notifications}
                unbookedRows={unbookedRows}
                unbookedLoading={unbooked.isLoading}
                projectsById={projectsById}
                locks={locks}
                lockNowMs={lockNowMs}
                nearby={nearby}
                nowMs={nowMs}
                search={mobileSearch}
                onSearch={setMobileSearch}
                restOpen={mobileRestOpen}
                onToggleRest={() => setMobileRestOpen(v => !v)}
                scrollContainer={mobileScrollRef}
                onOpenProject={onOpenProject}
                onPostpone={onPostpone}
                onSkip={onSkip}
                onNavigate={navigateTo}
                onMarkRead={markRead}
                onMarkAllRead={markAllRead}
                substitutions={substitutionPanel}
                layout={layout}
                onCustomize={() => setEditing(true)}
                customizeSheet={(
                    <MobileCustomizeSheet
                        open={editing}
                        onClose={() => setEditing(false)}
                        layout={layout}
                        saveState={saveState}
                        onRetrySave={retrySave}
                        onChoosePreset={choosePreset}
                        onToggleKpi={toggleTile}
                        onToggleWidget={toggleWidget}
                        onNudge={nudgeOnPhone}
                    />
                )}
            />
        );
    }

    // Ein Baustein je Kennung, mit allen Daten und Handlern wie bisher.
    // null heisst: gerade nichts zu zeigen (die Huelle faellt dann weg).
    const renderWidget = (id: WidgetId): React.ReactNode => {
        switch (id) {
            case 'kpis':
                return <KpiStrip kpis={kpis} tiles={layout.kpis} onSelect={onKpi} />;
            case 'agenda':
                return (
                    <AgendaPanel
                        agenda={agenda}
                        locks={locks}
                        lockNowMs={lockNowMs}
                        userId={userId}
                        onOpen={onOpenProject}
                        onPostpone={onPostpone}
                        onSkip={onSkip}
                        highlightKey={highlightKey}
                        groupRef={(key, el) => { if (el) groupEls.current.set(key, el); else groupEls.current.delete(key); }}
                    />
                );
            case 'notifications':
                return (
                    <Panel data-tutorial="dashboard.erwaehnungen" className="scroll-mt-3">
                        <PanelHeader
                            icon={<AtSign />}
                            title="Mitteilungen"
                            count={notifications.loading && notifications.items.length === 0 ? null : notifications.unreadCount}
                            countTone="blue"
                            sub={notifications.items.length > 0 ? `${notifications.items.length} gesamt` : undefined}
                            actions={notifications.items.length > 0 ? <LinkButton onClick={() => navigateTo('tasks')}>Alle</LinkButton> : undefined}
                        />
                        <NotificationList
                            items={notifications.items}
                            loading={notifications.loading}
                            error={notifications.error}
                            projects={visible}
                            onOpenProject={onOpenProject}
                            onMarkRead={markRead}
                            onMarkAllRead={markAllRead}
                            onRetry={notifications.refresh}
                            nowMs={nowMs}
                            limit={6}
                            compact
                            onShowAll={() => navigateTo('tasks')}
                        />
                    </Panel>
                );
            case 'tasks':
                return <TaskPanel tasks={flatTasks} totals={totals} limit={5} onOpen={onOpenProject} onShowAll={() => navigateTo('tasks')} planung={planung} onOpenPlanung={openPlanung} />;
            case 'unbooked':
                return <UnbookedPanel rows={unbookedRows} loading={unbooked.isLoading} projectsById={projectsById} onOpen={onOpenProject} limit={5} />;
            case 'projects':
                return (
                    <ProjectsPanel
                        rows={progressRows}
                        view={midView}
                        onViewChange={setMidView}
                        setServiceOverride={setGanttServiceOverride}
                        onOpen={onOpenProject}
                        expanded={projectsExpanded}
                        onToggleExpanded={() => setProjectsExpanded(v => !v)}
                        isMobile={false}
                        todayMs={nowMs}
                    />
                );
            case 'substitutions':
                return substitutionPanel;
            case 'sendcenter':
                return (
                    <SendCenterPanel
                        userId={userId}
                        open={showSende}
                        onToggle={() => setShowSende(v => !v)}
                        search={emailSearch}
                        onSearch={setEmailSearch}
                        projects={visible}
                    />
                );
        }
    };

    return (
        <div ref={desktopScrollRef} className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden ${glass ? 'glass-scope' : ''}`}>
            <div className={`max-w-[1600px] mx-auto px-4 lg:px-6 animate-fade-in ${glass ? 'py-6 space-y-5' : 'py-4 space-y-4'}`}>
                <DashboardHeader
                    greeting={greeting}
                    firstName={userProfile?.first_name || 'Nutzer'}
                    dateStr={dateStr}
                    kw={kw}
                    timeStr={timeStr}
                    stats={stats}
                    unread={notifications.unreadCount}
                    onBell={() => jumpTo('notifications', 'tasks')}
                    onCustomize={toggleEditing}
                    customizing={editing}
                />

                {editing && (
                    <DashboardCustomizer
                        layout={layout}
                        saveState={saveState}
                        onRetrySave={retrySave}
                        onChoosePreset={choosePreset}
                        onReset={resetLayout}
                        onToggleKpi={toggleTile}
                        onShow={show}
                        onDragStart={setDragging}
                        onDragEnd={endDrag}
                        onDone={() => setEditing(false)}
                    />
                )}

                <DashboardGrid
                    layout={layout}
                    editing={editing}
                    dragging={dragging}
                    renderWidget={renderWidget}
                    widgetRef={widgetRef}
                    onMove={moveTo}
                    onNudge={nudge}
                    onHide={hide}
                    onDragStart={setDragging}
                    onDragEnd={endDrag}
                />
            </div>
        </div>
    );
};
